import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell,
} from "recharts";
import { T, SERIES_COLORS, cardSx, labelSx, chartTooltipStyle, axisTick, StatTile, EmptyNote } from "./PlatformAnalyticsShared";

const ORIGIN_KEYS = ["form", "internal", "linkedin", "facebook", "google", "microsoft", "landbot"];

const CandidateSupplyTab = ({ data }) => {
  const { new_by_origin: byOrigin = [], cv_coverage: coverage, whitelist_mix: whitelist, availability = {} } = data;

  // Only render origin series that actually have data, to keep the legend clean.
  const activeOrigins = ORIGIN_KEYS.filter((k) => byOrigin.some((row) => (row[k] || 0) > 0));
  const availabilityData = Object.entries(availability)
    .filter(([, v]) => v > 0)
    .map(([key, value]) => ({ name: key.replace(/_/g, " "), value }));

  return (
    <Stack spacing={3}>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)" }, gap: "16px" }}>
        <StatTile label="Total candidates" value={coverage?.total ?? 0} />
        <StatTile label="With CV" value={coverage?.with_resume ?? 0} />
        <StatTile label="CV coverage" value={coverage?.coverage_pct ?? 0} suffix="%" warning={coverage != null && coverage.coverage_pct < 50} />
        <StatTile label="Whitelisted" value={whitelist?.whitelisted ?? 0} />
      </Box>

      <Box sx={cardSx}>
        <Typography sx={{ ...labelSx, mb: 1.5 }}>New candidates by signup origin (last 12 months)</Typography>
        {byOrigin.length > 0 && activeOrigins.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={byOrigin} margin={{ top: 4, right: 8, bottom: 0, left: -16 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(140 8% 90%)" vertical={false} />
              <XAxis dataKey="month" tick={axisTick} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={axisTick} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={chartTooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 11, fontFamily: "Segoe UI Regular, sans-serif" }} />
              {activeOrigins.map((origin, i) => (
                <Bar key={origin} dataKey={origin} name={origin} stackId="origin" fill={SERIES_COLORS[i % SERIES_COLORS.length]} maxBarSize={36} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <EmptyNote />
        )}
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "repeat(2, 1fr)" }, gap: "16px" }}>
        <Box sx={cardSx}>
          <Typography sx={{ ...labelSx, mb: 1.5 }}>Availability mix</Typography>
          {availabilityData.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={availabilityData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} paddingAngle={2}>
                  {availabilityData.map((d, i) => (
                    <Cell key={d.name} fill={SERIES_COLORS[i % SERIES_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={chartTooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 11, fontFamily: "Segoe UI Regular, sans-serif" }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <EmptyNote />
          )}
        </Box>

        <Box sx={cardSx}>
          <Typography sx={{ ...labelSx, mb: 1.5 }}>Whitelist status</Typography>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie
                data={[
                  { name: "Whitelisted", value: whitelist?.whitelisted ?? 0 },
                  { name: "Not whitelisted", value: whitelist?.not_whitelisted ?? 0 },
                ]}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={55}
                outerRadius={90}
                paddingAngle={2}
              >
                <Cell fill={T.green} />
                <Cell fill={T.dim} />
              </Pie>
              <Tooltip contentStyle={chartTooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 11, fontFamily: "Segoe UI Regular, sans-serif" }} />
            </PieChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    </Stack>
  );
};

export default CandidateSupplyTab;
